import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { School, Plus, Trash2, Edit3, X, Calendar, Clock, Link as LinkIcon, User, Users, BookOpen, Save } from "lucide-react";

const emptyForm = () => ({
  name: "",
  description: "",
  student_ids: [],
  course_ids: [],
  meeting_date: "",
  meeting_start: "",
  meeting_end: "",
  meeting_url: "",
  teacher_name: "",
  recording_url: "",
});

export default function ClassroomsAdmin() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null); // classroom object or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [studentSearch, setStudentSearch] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const [c, u, cs] = await Promise.all([
      api.get("/classrooms"),
      api.get("/users"),
      api.get("/courses"),
    ]);
    setItems(c.data);
    setUsers(u.data.filter((x) => x.role === "student"));
    setCourses(cs.data);
  };

  useEffect(() => { load(); }, []);

  const flash = (t, isErr = false) => {
    if (isErr) { setErr(t); setMsg(""); } else { setMsg(t); setErr(""); }
    setTimeout(() => { setErr(""); setMsg(""); }, 2500);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (cl) => {
    setEditing(cl);
    setForm({
      name: cl.name || "",
      description: cl.description || "",
      student_ids: cl.student_ids || [],
      course_ids: cl.course_ids || [],
      meeting_date: cl.meeting_date || "",
      meeting_start: cl.meeting_start || "",
      meeting_end: cl.meeting_end || "",
      meeting_url: cl.meeting_url || "",
      teacher_name: cl.teacher_name || "",
      recording_url: cl.recording_url || "",
    });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setForm(emptyForm()); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/classrooms/${editing.id}`, form);
        flash("Salón actualizado");
      } else {
        await api.post("/classrooms", form);
        flash("Salón creado y alumnos matriculados");
      }
      closeForm();
      load();
    } catch (e) { flash(formatApiError(e.response?.data?.detail), true); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("¿Eliminar salón? Se removerán inscripciones sin progreso.")) return;
    await api.delete(`/classrooms/${id}`);
    flash("Salón eliminado");
    load();
  };

  const toggleId = (list, id) => list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const filteredStudents = users.filter((u) => {
    const s = studentSearch.toLowerCase();
    return !s || u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
  });
  const filteredCourses = courses.filter((c) => {
    const s = courseSearch.toLowerCase();
    return !s || c.title.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6 animate-fade-in">
      {(msg || err) && (
        <div className={`px-3 py-2 rounded-lg text-sm ${err ? "bg-red-100 text-red-700 border border-red-200" : "bg-green-100 text-green-800 border border-green-200"}`}>
          {err || msg}
        </div>
      )}

      {!showForm && (
        <>
          <div className="flex justify-between items-center flex-wrap gap-3">
            <h2 className="font-heading font-bold text-marino text-xl flex items-center gap-2">
              <School className="w-5 h-5 text-naranja" /> Salones virtuales
            </h2>
            <button data-testid="classroom-create-btn" onClick={openCreate} className="bg-naranja text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover inline-flex items-center gap-1">
              <Plus className="w-4 h-4" /> Nuevo salón
            </button>
          </div>

          {items.length === 0 ? (
            <div className="bg-white border border-gris/30 rounded-xl p-10 text-center">
              <School className="w-12 h-12 text-gris mx-auto mb-3" />
              <p className="text-marino/70">Aún no hay salones creados. Crea el primero para matricular alumnos masivamente.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((cl) => (
                <div key={cl.id} data-testid={`classroom-card-${cl.id}`} className="bg-white border border-gris/30 rounded-xl overflow-hidden shadow-sm">
                  <div className="bg-marino text-white p-4">
                    <h3 className="font-heading font-bold truncate">{cl.name}</h3>
                    {cl.description && <p className="text-white/70 text-xs mt-1 line-clamp-2">{cl.description}</p>}
                  </div>
                  <div className="p-4 space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-marino/80"><Users className="w-4 h-4 text-naranja" /> {cl.student_count} alumnos</div>
                    <div className="flex items-center gap-2 text-marino/80"><BookOpen className="w-4 h-4 text-naranja" /> {cl.course_count} cursos</div>
                    {cl.meeting_date && (
                      <div className="flex items-center gap-2 text-marino/80"><Calendar className="w-4 h-4 text-naranja" /> {cl.meeting_date} {cl.meeting_start && `· ${cl.meeting_start}`}</div>
                    )}
                    {cl.teacher_name && <div className="flex items-center gap-2 text-marino/80"><User className="w-4 h-4 text-naranja" /> {cl.teacher_name}</div>}
                    <div className="flex gap-2 pt-3">
                      <button data-testid={`classroom-edit-${cl.id}`} onClick={() => openEdit(cl)} className="flex-1 bg-gris text-marino px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-grisHover inline-flex items-center justify-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Editar</button>
                      <button data-testid={`classroom-delete-${cl.id}`} onClick={() => removeItem(cl.id)} className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-700 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-gris/30 rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-marino text-xl">{editing ? "Editar salón" : "Nuevo salón"}</h3>
            <button type="button" data-testid="classroom-cancel" onClick={closeForm} className="text-marino/70 hover:text-marino"><X className="w-5 h-5" /></button>
          </div>

          {/* Datos básicos */}
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="classroom-name" required placeholder="Nombre del salón (ej: Generación 2026 - Grupo A)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
            <input data-testid="classroom-description" placeholder="Descripción (opcional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
          </div>

          {/* Selector alumnos */}
          <div>
            <label className="block text-sm font-medium text-marino mb-2 flex items-center gap-1"><Users className="w-4 h-4 text-naranja" /> Alumnos ({form.student_ids.length} seleccionados)</label>
            <input data-testid="classroom-student-search" value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} placeholder="Buscar por nombre o email..." className="w-full px-3 py-2 border border-gris rounded-lg text-sm mb-2" />
            <div className="max-h-56 overflow-y-auto border border-gris/40 rounded-lg divide-y divide-gris/20">
              {filteredStudents.length === 0 && <div className="p-3 text-sm text-marino/60">Sin resultados</div>}
              {filteredStudents.map((u) => {
                const sel = form.student_ids.includes(u.id);
                return (
                  <label key={u.id} data-testid={`student-opt-${u.id}`} className={`flex items-center gap-2 p-2 cursor-pointer text-sm ${sel ? "bg-naranja/5" : "hover:bg-marino/5"}`}>
                    <input type="checkbox" checked={sel} onChange={() => setForm({ ...form, student_ids: toggleId(form.student_ids, u.id) })} />
                    <span className="text-marino font-medium">{u.name}</span>
                    <span className="text-marino/60 text-xs">{u.email}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Selector cursos */}
          <div>
            <label className="block text-sm font-medium text-marino mb-2 flex items-center gap-1"><BookOpen className="w-4 h-4 text-naranja" /> Cursos ({form.course_ids.length} seleccionados)</label>
            <input data-testid="classroom-course-search" value={courseSearch} onChange={(e) => setCourseSearch(e.target.value)} placeholder="Buscar curso..." className="w-full px-3 py-2 border border-gris rounded-lg text-sm mb-2" />
            <div className="max-h-56 overflow-y-auto border border-gris/40 rounded-lg divide-y divide-gris/20">
              {filteredCourses.length === 0 && <div className="p-3 text-sm text-marino/60">Sin resultados</div>}
              {filteredCourses.map((c) => {
                const sel = form.course_ids.includes(c.id);
                return (
                  <label key={c.id} data-testid={`course-opt-${c.id}`} className={`flex items-center gap-2 p-2 cursor-pointer text-sm ${sel ? "bg-naranja/5" : "hover:bg-marino/5"}`}>
                    <input type="checkbox" checked={sel} onChange={() => setForm({ ...form, course_ids: toggleId(form.course_ids, c.id) })} />
                    <span className="text-marino font-medium">{c.title}</span>
                    <span className="text-marino/60 text-xs">${c.price} · {c.duration_hours}h</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Teams meeting */}
          <div className="border-t border-gris/30 pt-5">
            <h4 className="font-heading font-bold text-marino mb-3 flex items-center gap-2"><LinkIcon className="w-4 h-4 text-naranja" /> Sesión de Microsoft Teams</h4>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-marino/70 mb-1"><Calendar className="w-3.5 h-3.5 inline" /> Fecha</label>
                <input data-testid="meeting-date" type="date" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} className="w-full px-3 py-2 border border-gris rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs text-marino/70 mb-1"><Clock className="w-3.5 h-3.5 inline" /> Inicio</label>
                <input data-testid="meeting-start" type="time" value={form.meeting_start} onChange={(e) => setForm({ ...form, meeting_start: e.target.value })} className="w-full px-3 py-2 border border-gris rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs text-marino/70 mb-1"><Clock className="w-3.5 h-3.5 inline" /> Fin</label>
                <input data-testid="meeting-end" type="time" value={form.meeting_end} onChange={(e) => setForm({ ...form, meeting_end: e.target.value })} className="w-full px-3 py-2 border border-gris rounded-lg text-sm" />
              </div>
              <input data-testid="meeting-url" placeholder="https://teams.microsoft.com/l/meetup-join/..." value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} className="sm:col-span-2 px-3 py-2 border border-gris rounded-lg text-sm" />
              <input data-testid="meeting-teacher" placeholder="Instructor asignado" value={form.teacher_name} onChange={(e) => setForm({ ...form, teacher_name: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
              <input data-testid="meeting-recording" placeholder="🎥 URL de grabación (agregar tras la sesión)" value={form.recording_url} onChange={(e) => setForm({ ...form, recording_url: e.target.value })} className="sm:col-span-3 px-3 py-2 border border-gris rounded-lg text-sm" />
            </div>
            <p className="text-xs text-marino/60 mt-2">💡 Pega el enlace de la reunión de Teams. El botón "Ingresar" del alumno se activará 15 min antes del inicio. Los alumnos recibirán un recordatorio por email ~1 hora antes.</p>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t border-gris/30">
            <button type="button" onClick={closeForm} className="bg-gris text-marino px-4 py-2 rounded-lg text-sm font-medium hover:bg-grisHover">Cancelar</button>
            <button data-testid="classroom-submit" className="bg-naranja text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover inline-flex items-center gap-1">
              <Save className="w-4 h-4" /> {editing ? "Guardar cambios" : "Crear salón y matricular"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
